import { useState, useEffect } from "react";
import Select from "@/components/molecules/Select";
import Button from "@/components/atoms/Button";
import Ticket from "@/types/Ticket";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import technicalQaSchema from "@/schemas/technicalQa";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/atoms/Form";
import CKEditorWithFileUpload from "@/components/atoms/CKEditor";
import FileUpload from "@/components/atoms/FileUpload";
import { Paperclip } from "lucide-react";
import axios from "axios";
import useAccess from "@/hooks/useAccess";
import Attachment from "@/types/Attachment";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { router } from "@inertiajs/react";

type Props = {
  ticket: Ticket;
  setSelectedTicketStatus: (status: string) => void;
  qualityTickets: any[];
};

const TechnicalQualityFrom = ({ ticket, setSelectedTicketStatus, qualityTickets }: Props) => {
  const [lastQaTicket, setLastQaTicket] = useState<any>(qualityTickets.filter((qa) => qa.passed === null && qa.issue === null).pop() || null);
  const [technicalQaTickets, setTechnicalQaTickets] = useState<any>(qualityTickets.filter((qa) => qa.passed === null && qa.issue !== null) || null);
	const [editorData, setEditorData] = useState("");
	const [fileUploadKey, setFileUploadKey] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const { currentUser } = useAccess();

  const form = useForm({
    resolver: zodResolver(technicalQaSchema),
    defaultValues: {
			qa_issue: "",
			qa_comment: "",
			files: [],
		}
  });

  const formatDate = (input: string): string => {
    const date = new Date(input.replace(/(\d+)\/(\d+)\/(\d+)/, '$2/$1/$3'));
    
    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };
  
    return date.toLocaleDateString('en-GB', options).replace(',', '');
  };

  const handleSubmit = async (data: any) => {
		setIsSubmitting(true);

    const payload = {
      ticket: {
        qa_issue: data.qa_issue === "true",
        qa_comment: data.qa_comment
      },
      files: data.files.map((file: any) => ({
        filename: file.name,
        filesize: file.size,
        filetype: file.content_type,
        filepath: file.path,
        original_name: file.original_name,
        fileurl: file.url,
      })),
    };

    try {
			router.patch(
				`/tickets/${ticket.id}/technical_qa`,
				payload,
				{
					onSuccess: (page) => {
						setFileUploadKey((prevKey) => prevKey + 1);
						setEditorData("");
            form.reset({
              qa_issue: "",
              qa_comment: "",
              files: [],
            });
					},
					onError: (errors) => {
						setIsSubmitting(false);
					},
					onFinish: () => {
						setIsSubmitting(false);
					},
				}
			);
		} catch (error) {
			setIsSubmitting(false);
		}
	}

  useEffect(() => {
		const newLastQaTicket =
    qualityTickets.filter((qa) => qa.passed === null && qa.issue === null).pop() || null;
		setLastQaTicket(newLastQaTicket);
    setTechnicalQaTickets(qualityTickets.filter((qa) => qa.passed === null && qa.issue !== null) || null);
		setSelectedTicketStatus(ticket.status.id.toString());
	}, [qualityTickets]);

  const handleEditorChange = (data: string) => {
    setEditorData(data);
    form.setValue('qa_comment', data);
  };

  return (
    <div>
      {lastQaTicket && currentUser && lastQaTicket.assignee.id === currentUser.id ? (
				<>
					<div className="shadow-lg rounded-md p-6 w-full border border-border">
            <div className='bg-gray-100 dark:bg-background shadow-md rounded-md'>
              {/* Info */}
              <div className="flex-col gap-4 py-8 px-4">
                <div className='flex items-start'>
                  <div className='w-60'>
                    <label className="text-sm text-gray-500 dark:text-darkTextColor">Comment for assignee</label>
                  </div>
                  <div className='shadow-xs rounded-md w-full border border-border'>
                    <div className='py-4 px-4'>
                      <div className="mb-4">
                        <div className="flex items-center space-x-3">
                          <img
                            src={"avatar_url" in lastQaTicket.assigned_by ? (lastQaTicket.assigned_by.avatar_url) : "/default-avatar.png"}
                            alt="Profile"
                            className="w-8 h-8 rounded-full dark:bg-darkTextColor"
                          />
                          <div className='flex gap-2 items-center'>
                            <div>
                              <h4 className="font-semibold text-gray-800 dark:text-darkTextColor">{lastQaTicket.assigned_by.name}</h4>
                            </div>
                            <div>
                              <span className="text-sm text-gray-500 dark:text-darkTextColor">{formatDate(lastQaTicket?.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mb-4">
                        <div className="mt-4 ml-12">
                          <div
                            className="text-gray-700 dark:text-darkTextColor"
                            dangerouslySetInnerHTML={{ __html: lastQaTicket.comment_for_assignee }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='flex items-start mt-8'>
                  <div className='w-40'>
                    <label className="text-sm text-gray-500 dark:text-darkTextColor">Attachments</label>
                  </div>
                  <div>
                    {lastQaTicket.attachments && lastQaTicket.attachments.length > 0 && (
                      <div className="mt-4">
                        <ul className="mt-2">
                          {lastQaTicket?.attachments?.map((attachment: Attachment) => (
                            <div key={attachment.id} className="flex">
                              <Paperclip size={24} className="text-blue-500 mr-2" />
                              <a
                                href={attachment.url || ''}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-bold text-blue-500 hover:underline block"
                              >
                                {attachment.original_name || attachment.name}
                              </a>
                            </div>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="px-4 pb-4">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
                    
                  <FormField
                    control={form.control}
                    name="qa_issue"
                    render={({ field }) => (
                      <FormItem className="w-1/5">
                        <FormLabel>Is there any issues? *</FormLabel>
                        <FormControl>
                          <RadioGroup.Root
                            className="flex gap-4 mt-2"
                            value={field.value.toString()}
                            onValueChange={(value) => field.onChange(value)}
                          >
                            <RadioGroup.Item
                              value="true"
                              id="qa_yes"
                              className="w-6 h-6 rounded-full border border-gray-400 flex items-center justify-center cursor-pointer 
                                        focus:outline-none focus:ring-2 focus:ring-primary data-[state=checked]:bg-primary"
                            >
                              <RadioGroup.Indicator className="w-3 h-3 rounded-full" />
                            </RadioGroup.Item>
                            <label htmlFor="qa_yes" className="text-sm cursor-pointer">
                              Yes
                            </label>

                            <RadioGroup.Item
                              value="false"
                              id="qa_no"
                              className="w-6 h-6 rounded-full border border-gray-400 flex items-center justify-center cursor-pointer 
                                        focus:outline-none focus:ring-2 focus:ring-primary data-[state=checked]:bg-primary"
                            >
                              <RadioGroup.Indicator className="w-3 h-3 rounded-full" />
                            </RadioGroup.Item>
                            <label htmlFor="qa_no" className="text-sm cursor-pointer">
                              No
                            </label>
                          </RadioGroup.Root>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                
                    <FormField
                      control={form.control}
                      name="qa_comment"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormLabel></FormLabel>
                          <FormControl>
                            <CKEditorWithFileUpload
                              value={editorData}
                              onChange={handleEditorChange}
                              isPublic={true}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="files"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Attachments</FormLabel>
                          <FormControl>
                            <FileUpload
                              key={fileUploadKey}
                              files={field.value || []}
                              onChange={(files) => field.onChange(files)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                      
                    <div className="flex gap-2">
                      <Button type="submit" disabled={isSubmitting}>
                        Save
                      </Button>
                    </div>
                  </form>
                </Form>

              </div>
                
            </div>

          </div>
				</>
				
			): (
        <>
          {technicalQaTickets && technicalQaTickets.length > 0 ? (
            <div className="shadow-lg rounded-md p-6 w-full border border-border">
              <div className='bg-gray-100 dark:bg-background  shadow-md rounded-md'>
                {technicalQaTickets.map((technicalQaTicket: any) => (
                  <div key={technicalQaTicket.id} className="flex-col gap-4 py-8 px-4">
                    <div className='flex items-start mb-4'>
                      <div className='w-60'>
                        <label className="text-sm text-gray-500 dark:text-darkTextColor">Comment for assignee</label>
                      </div>
                      <div className='bg-white dark:bg-background shadow-xs rounded-md w-full border border-border'>
                        <div className='py-4 px-4'>
                          <div className="mb-4">
                            <div className="flex items-center space-x-3">
                              <img
                                src={"avatar_url" in technicalQaTicket.assigned_by ? (technicalQaTicket.assigned_by.avatar_url) : "/default-avatar.png"}
                                alt="Profile"
                                className="w-8 h-8 rounded-full dark:bg-darkTextColor"
                              />
                              <div className='flex gap-2 items-center'>
                                <div>
                                  <h4 className="font-semibold text-gray-800 dark:text-darkTextColor">{technicalQaTicket.assigned_by.name}</h4>
                                </div>
                                <div>
                                  <span className="text-sm text-gray-500 dark:text-darkTextColor">{formatDate(technicalQaTicket?.created_at)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mb-4">
                            <div className="mt-4 ml-12">
                              <div
                                className="text-gray-700 dark:text-darkTextColor"
                                dangerouslySetInnerHTML={{ __html: technicalQaTicket.comment_for_assignee }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='flex items-start'>
                      <div className='w-60'>
                        <label className="text-sm text-gray-500 dark:text-darkTextColor">Comment for QA checker</label>
                      </div>
                      <div className='bg-white dark:bg-background  shadow-xs rounded-md w-full border border-border'>
                        <div className='py-4 px-4'>
                          <div className="mb-4">
                            <div className="flex items-center space-x-3">
                              <img
                                src={"avatar_url" in technicalQaTicket.assignee ? (technicalQaTicket.assignee.avatar_url) : "/default-avatar.png"}
                                alt="Profile"
                                className="w-8 h-8 rounded-full dark:bg-darkTextColor"
                              />
                              <div className='flex gap-2 items-center'>
                                <div>
                                  <h4 className="font-semibold text-gray-800 dark:text-darkTextColor">{technicalQaTicket.assignee.name}</h4>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mb-4">
                            <div className="mt-4 ml-12">
                              <div
                                className="text-gray-700 dark:text-darkTextColor"
                                dangerouslySetInnerHTML={{ __html: technicalQaTicket.comment }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='flex items-start mt-8'>
                      <div className='w-40'>
                        <label className="text-sm text-gray-500 dark:text-darkTextColor">Issue?</label>
                      </div>
                      <div><span>{technicalQaTicket.issue ? "Yes" : "No"}</span></div>
                    </div>
                    <div className='flex items-start mt-8'>
                      <div className='w-40'>
                        <label className="text-sm text-gray-500 dark:text-darkTextColor">Attachments</label>
                      </div>
                      <div>
                        {technicalQaTicket.attachments && technicalQaTicket.attachments.length > 0 && (
                          <div className="mt-4">
                            <ul className="mt-2">
                              {technicalQaTicket?.attachments?.map((attachment: Attachment) => (
                                <div key={attachment.id} className="flex">
                                  <Paperclip size={24} className="text-blue-500 mr-2" />
                                  <a
                                    href={attachment.url || ''}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-bold text-blue-500 hover:underline block dark:text-darkTextColor"
                                  >
                                    {attachment.original_name || attachment.name}
                                  </a>
                                </div>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ): (
            <div>Not Checked</div>
          )}
        </>
			)}
    </div>
  );
};

export default TechnicalQualityFrom;